import { useSyncExternalStore } from "react";

// Tailwind v4 breakpoints (rem)
const breakpoints = {
  xs: "30rem",
  sm: "40rem",
  md: "48rem",
  lg: "64rem",
  xl: "80rem",
  "2xl": "96rem",
} as const;

type Breakpoint = keyof typeof breakpoints;

interface MediaQueryStore {
  getSnapshot: () => boolean;
  getServerSnapshot: () => boolean;
  subscribe: (callback: () => void) => () => void;
}

const storeCache = new Map<string, MediaQueryStore>();

const createMediaQueryStore = (query: string): MediaQueryStore => {
  const cached = storeCache.get(query);
  if (cached) return cached;

  const mql = typeof window !== "undefined" ? window.matchMedia(query) : null;

  const store: MediaQueryStore = {
    getSnapshot: () => mql?.matches ?? false,
    getServerSnapshot: () => false,
    subscribe: (callback: () => void) => {
      mql?.addEventListener("change", callback);
      return () => mql?.removeEventListener("change", callback);
    },
  };

  storeCache.set(query, store);
  return store;
};

/**
 * Returns true if viewport width >= breakpoint
 */
export const useBreakpoint = (breakpoint: Breakpoint): boolean => {
  const query = `(min-width: ${breakpoints[breakpoint]})`;
  const store = createMediaQueryStore(query);
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getServerSnapshot);
};

/**
 * Returns current breakpoint name
 */
export const useCurrentBreakpoint = (): Breakpoint | null => {
  const is2xl = useBreakpoint("2xl");
  const isXl = useBreakpoint("xl");
  const isLg = useBreakpoint("lg");
  const isMd = useBreakpoint("md");
  const isSm = useBreakpoint("sm");
  const isXs = useBreakpoint("xs");

  if (is2xl) return "2xl";
  if (isXl) return "xl";
  if (isLg) return "lg";
  if (isMd) return "md";
  if (isSm) return "sm";
  if (isXs) return "xs";
  return null;
};
